import './home.scss'

import { Component, For, Match, Show, Switch, createMemo, createSignal, onMount } from 'solid-js'
import {
  ComponentEventEmitter,
  createEmitter,
  getAssetUrl,
  setComponentPageTitle,
  uniqueBy,
  storage,
} from '../../shared/util'
import {
  announceStore,
  characterStore,
  chatStore,
  pageStore,
  settingStore,
  userStore,
  i18nStore,
} from '../../store'
import { A, useNavigate } from '@solidjs/router'
import { AlertTriangle, MoveRight, Plus, Settings } from 'lucide-solid'
import { Card, Pill, SolidCard, TitleCard } from '/web/shared/Card'
import Modal from '/web/shared/Modal'
import AvatarIcon from '/web/shared/AvatarIcon'
import { elapsedSince } from '/common/util'
import { AppSchema } from '/common/types'
import { markdown } from '/web/shared/markdown'
import WizardIcon from '/web/icons/WizardIcon'
import Slot from '/web/shared/Slot'
import { adaptersToOptions } from '/common/adapters'
import { useRef } from '/web/shared/hooks'
import { canStartTour, startTour } from '/web/tours'
import WelcomeModal from '/web/shared/WelcomeModal'
import DiscoveryPage from './DiscoveryPage'

const enum Sub {
  None,
  OpenAI,
  NovelAI,
  Horde,
}

const WELCOME_MODAL_KEY = 'welcome_modal_seen'

const HomePage: Component = () => {
  const [ref, onRef] = useRef()
  const i18n = i18nStore((s) => ({ t: s.t }))
  setComponentPageTitle(i18n.t.admin_home)
  const [sub, setSub] = createSignal(Sub.None)
  const [showWelcome, setShowWelcome] = createSignal(false)

  const closeSub = () => setSub(Sub.None)

  const user = userStore((s) => ({ userLevel: s.userLevel, loggedIn: s.loggedIn }))
  const announce = announceStore((s) => ({ list: s.list }))
  const cfg = settingStore((cfg) => ({
    initLoading: cfg.initLoading,
    adapters: adaptersToOptions(cfg.config.adapters),
    guest: cfg.guestAccessAllowed,
    config: cfg.config,
  }))

  const announcements = createMemo(() => {
    return announce.list.filter((ann) => {
      if (ann.location && ann.location !== 'home') return false
      const level = ann.userLevel ?? -1
      return user.userLevel >= level
    })
  })

  const emitter = createEmitter('loaded')

  onMount(() => {
    announceStore.getAll()

    // Show welcome modal if user is not logged in
    if (!user.loggedIn) {
      setShowWelcome(true)
    }

    emitter.on('loaded', () => {
      if (!canStartTour('home')) return
      pageStore.menu(true)
      startTour('home')
    })
  })

  const handleWelcomeClose = () => {
    // Don't close if user is not logged in - they need to login or register
    if (user.loggedIn) {
      storage.localSetItem(WELCOME_MODAL_KEY, 'true')
      setShowWelcome(false)
    }
  }

  return (
    <div>

      {/* <Show when={!cfg.guest}>
        <div class="flex text-orange-500" role="alert">
          <AlertTriangle class="mb-2 mr-2" aria-hidden="true" />
          {i18n.t.home_browserNoStorage}
        </div>
      </Show> */}

      {/* Show normal home page for logged-in users */}
      <Show when={user.loggedIn}>
        <DiscoveryPage />

        <Switch>
          <Match when={sub() === Sub.Horde}>
            <HordeGuide close={closeSub} />
          </Match>

          <Match when={sub() === Sub.OpenAI}>
            <OpenAIGuide close={closeSub} />
          </Match>
        </Switch>
      </Show>

      <Show when={!user.loggedIn}>
        <WelcomeModal show={showWelcome()} onClose={handleWelcomeClose} />
      </Show>
    </div>
  )
}

export default HomePage

const RecentChats: Component<{ emitter: ComponentEventEmitter<'loaded'> }> = (props) => {
  const nav = useNavigate()
  const i18n = i18nStore((s) => ({ t: s.t }))

  const chars = characterStore((s) => ({ map: s.characters.map }))

  const state = chatStore((s) => {
    // We want this to occur after the state has propogated
    setTimeout(() => props.emitter.emit.loaded(), 200)

    return {
      last: uniqueBy(s.allChats, 'characterId')
        .slice()
        .sort((l, r) => (r.updatedAt > l.updatedAt ? 1 : -1))
        .slice(0, 4)
        .map((chat) => ({ chat, char: chars.map[chat.characterId] }))
        .filter((pair) => !!pair.char),
    }
  })

  return (
    <section class="flex flex-col" aria-labelledby="homeRecConversations">
      <div id="homeRecConversations" class="text-lg font-bold" aria-hidden="true">
        {i18n.t.recentConversations}
      </div>
      <div
        class="grid w-full grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-4"
        classList={{ hidden: state.last.length === 0 }}
      >
        <For each={state.last}>
          {({ chat, char }, index) => (
            <>
              <div
                role="link"
                aria-label={`${i18n.t.chat_chatWith} ${char.name}, ${elapsedSince(chat.updatedAt)} ${
                  i18n.t.time_ago.replace('{duration}', '')
                } ${
                  chat.name
                }`}
                classList={{ 'tour-first-chat': index() === 0 }}
                class="bg-800 hover:bg-700 hidden h-24 w-full cursor-pointer rounded-md border-[1px] border-[var(--bg-700)] transition duration-300 sm:flex"
                onClick={() => nav(`/chat/${chat._id}`)}
              >
                <Show when={char?.avatar}>
                  <AvatarIcon
                    noBorder
                    class="flex items-center justify-start"
                    format={{ corners: 'md', size: 'max3xl' }}
                    avatarUrl={getAssetUrl(char?.avatar || '')}
                  />
                </Show>

                <Show when={!char?.avatar}>
                  <div class="flex h-24 w-24 items-center justify-center">
                    <AvatarIcon
                      noBorder
                      format={{ corners: 'md', size: 'xl' }}
                      avatarUrl={getAssetUrl(char?.avatar || '')}
                    />
                  </div>
                </Show>

                <div class="flex w-full flex-col justify-between text-sm" aria-hidden="true">
                  <div class="flex flex-col px-1">
                    <div class="text-sm font-bold">{char.name}</div>
                    <div class="text-500 text-xs">{elapsedSince(chat.updatedAt)}</div>
                    <Show when={chat.name}>
                      <p class="line-clamp-2 max-h-10 overflow-hidden text-ellipsis">{chat.name}</p>
                    </Show>
                  </div>
                  <div class="flex max-h-10 w-full items-center justify-end px-2">
                    {/* <div class="flex items-center"> */}
                    <MoveRight size={14} />
                    {/* </div> */}
                  </div>
                </div>
              </div>

              <div
                role="link"
                aria-label={`${i18n.t.chat_chatWith} ${char.name}, ${elapsedSince(chat.updatedAt)} ${
                  i18n.t.time_ago.replace('{duration}', '')
                } ${
                  chat.name
                }`}
                classList={{ 'tour-first-chat-mobile': index() === 0 }}
                class="bg-800 hover:bg-700 flex w-full cursor-pointer flex-col rounded-md border-[1px] border-[var(--bg-700)] transition duration-300 sm:hidden"
                onClick={() => nav(`/chat/${chat._id}`)}
              >
                <div class="flex" aria-hidden="true">
                  <div class="flex items-center justify-center px-1 pt-1">
                    <AvatarIcon
                      noBorder
                      format={{ corners: 'circle', size: 'md' }}
                      avatarUrl={getAssetUrl(char?.avatar || '')}
                    />
                  </div>
                  <div class="flex flex-col overflow-hidden text-ellipsis whitespace-nowrap px-1">
                    <div class="overflow-hidden text-ellipsis text-sm font-bold">{char.name}</div>
                    <div class="text-500 text-xs">{elapsedSince(chat.updatedAt)}</div>
                  </div>
                </div>

                <div class="flex h-full w-full flex-col justify-between text-sm" aria-hidden="true">
                  <p class="line-clamp-2 max-h-10 overflow-hidden text-ellipsis px-1">
                    {chat.name}
                  </p>

                  <div class="flex max-h-10 w-full items-center justify-end px-2">
                    {/* <div class="flex items-center"> */}
                    <MoveRight size={14} />
                    {/* </div> */}
                  </div>
                </div>
              </div>
            </>
          )}
        </For>
        <Show when={state.last.length < 4}>
          <BorderCard href="/chats/create" ariaLabel={i18n.t.startConversation}>
            <div aria-hidden="true">{i18n.t.startConversation}</div>
            <Plus size={20} aria-hidden="true" />
          </BorderCard>
        </Show>

        <Show when={state.last.length < 3}>
          <BorderCard href="/editor" ariaLabel={i18n.t.createCharacter}>
            <div aria-hidden="true">{i18n.t.createCharacter}</div>
            <WizardIcon size={20} aria-hidden="true" />
          </BorderCard>
        </Show>

        <Show when={state.last.length < 2}>
          <BorderCard href="/settings" ariaLabel={i18n.t.configureYourAIServices}>
            <div class="flex w-full items-center justify-center text-center" aria-hidden="true">
              {i18n.t.configureYourAIServices}
            </div>
            <Settings size={20} aria-hidden="true" />
          </BorderCard>
        </Show>
      </div>
    </section>
  )
}

const BorderCard: Component<{ children: any; href: string; ariaLabel?: string }> = (props) => {
  const nav = useNavigate()
  return (
    <div
      role="button"
      aria-label={props.ariaLabel}
      class="bg-800 text-700 hover:bg-600 flex h-24 w-full cursor-pointer flex-col items-center justify-center border-[2px] border-dashed border-[var(--bg-700)] text-center transition duration-300"
      onClick={() => nav(props.href)}
    >
      {props.children}
    </div>
  )
}

const Announcements: Component<{ list: AppSchema.Announcement[] }> = (props) => {
  return (
    <>
      <section class="flex flex-col gap-4" aria-labelledby="homeAnnouncements">
        <For each={props.list}>
          {(item, i) => (
            <div class="rounded-md border-[1px] border-[var(--hl-700)]">
              <div class="flex flex-col rounded-t-md bg-[var(--hl-800)] p-2">
                <div class="text-lg font-bold" role="heading">
                  {item.title}
                </div>
                {/* <div class="text-700 text-xs">{elapsedSince(item.showAt)} ago</div> */}
              </div>
              <div
                class="rendered-markdown bg-900 rounded-b-md p-2"
                innerHTML={markdown.makeHtml(item.content)}
              ></div>
            </div>
          )}
        </For>
      </section>
    </>
  )
}

const Features: Component = () => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  return (
    <Card border>
      <section aria-labelledby="homeNotableFeats">
        <div id="homeNotableFeats" class="flex justify-center text-xl font-bold" aria-hidden="true">
          {i18n.t.notableFeatures}
        </div>
        <div class="flex flex-col gap-2 leading-6">
          <p>{i18n.t.home_free_to_use}</p>
          <p>
            <b class="highlight">{i18n.t.register}</b> {i18n.t.yourDataWillBeStored}
          </p>
          <p>{i18n.t.home_chat_multi_user}</p>
          <p>
            {i18n.t.home_create_memory_book.split('{memoryBook}')[0]}
            <b class="highlight">{i18n.t.memoryBook}</b>
            {i18n.t.home_create_memory_book.split('{memoryBook}')[1]}
          </p>
          <p>
            {i18n.t.home_use_images.split('{images}')[0]}
            <b class="highlight">{i18n.t.admin_images}</b>
            {i18n.t.home_use_images.split('{images}')[1]}
          </p>
          <p>
            {i18n.t.home_give_voice.split('{voice}')[0]}
            <b class="highlight">{i18n.t.voice}</b>
            {i18n.t.home_give_voice.split('{voice}')[1]}
          </p>
          <p>
            {i18n.t.home_customise_presets.split('{presets}')[0]}
            <b class="highlight">{i18n.t.presets}</b>
            {i18n.t.home_customise_presets.split('{presets}')[1]}
          </p>
        </div>
      </section>
    </Card>
  )
}

const HordeGuide: Component<{ close: () => void }> = (props) => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  return (
    <Modal show close={props.close} title={i18n.t.home_horde_guide} maxWidth="half" ariaLabel={i18n.t.home_horde_guide}>
      <div class="flex flex-col gap-2">
        <SolidCard bg="hl-900">
          {i18n.t.home_horde_important.split('{link}')[0]}
          <a href="https://aihorde.net/register" class="link" target="_blank">
            AI Horde
          </a>
          {i18n.t.home_horde_important.split('{link}')[1].split('{settings}')[0]}
          <A href="/settings?tab=ai&service=horde" class="link">
            {i18n.t.settings}
          </A>
          {i18n.t.home_horde_important.split('{settings}')[1]}
        </SolidCard>

        <SolidCard bg="hl-900">
          {i18n.t.home_horde_volunteers}
        </SolidCard>

        <Card>
          {i18n.t.home_horde_tokens.split('{maxNewTokens}')[0]}
          <b>{i18n.t.maxNewTokens}</b>
          {i18n.t.home_horde_tokens.split('{maxNewTokens}')[1]}
        </Card>
        <Card>
          {i18n.t.home_horde_anonymous}
        </Card>
      </div>
    </Modal>
  )
}

const OpenAIGuide: Component<{ close: () => void }> = (props) => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  return (
    <Modal show close={props.close} title={i18n.t.home_openai_guide} maxWidth="half" ariaLabel={i18n.t.home_openai_guide}>
      <div class="flex flex-col gap-2">
        <Card>
          {i18n.t.home_openai_paid.split('{paidService}')[0]}
          <b>{i18n.t.admin_off}</b>
          {i18n.t.home_openai_paid.split('{paidService}')[1]}
        </Card>

        <Card>
          {i18n.t.home_openai_register.split('{registerLink}')[0]}
          <A class="link" href="https://auth0.openai.com/u/signup" target="_blank">
            {i18n.t.register}
          </A>
          {i18n.t.home_openai_register.split('{registerLink}')[1]}
        </Card>

        <Card>
          {i18n.t.home_openai_generate_key.split('{generateLink}')[0]}
          <A class="link" href="https://platform.openai.com/account/api-keys" target="_blank">
            {i18n.t.settings_ai_generateKey}
          </A>
          {i18n.t.home_openai_generate_key.split('{generateLink}')[1]}
        </Card>

        <Card>
          {i18n.t.home_openai_set_key.split('{settingsLink}')[0]}
          <A class="link" href="/settings?tab=ai&service=openai">
            {i18n.t.settings}
          </A>
          {i18n.t.home_openai_set_key.split('{settingsLink}')[1]}
        </Card>

        <Card>
          {i18n.t.home_openai_preset.split('{chatPresetSettings}')[0]}
          <b>{i18n.t.settings_ai_apiAccessPreset}</b>
          {i18n.t.home_openai_preset.split('{chatPresetSettings}')[1]}
        </Card>
      </div>
    </Modal>
  )
}
